import { useState } from "react";
import { Phone, Mail, Clock, MapPin, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionHead } from "../components/Reveal";
import Seo from "../components/Seo";
import { API, INPUT_CLS, PHONE, PHONE_LINK, EMAIL } from "../data";

const INFO = [
  { icon: Phone, label: "Telefono", value: PHONE, href: PHONE_LINK, testid: "contact-phone-link" },
  { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-email-link" },
  { icon: Clock, label: "Orari", value: "Lun - Ven: 08:00 - 19:00 | Sab: 08:30 - 13:00", testid: "contact-hours" },
  { icon: MapPin, label: "Zona Operativa", value: "Interventi in tutta Italia", testid: "contact-area" },
];

const BUDGETS = ["< 10.000 €", "10.000 - 30.000 €", "30.000 - 80.000 €", "80.000 - 150.000 €", "> 150.000 €"];

export default function Contatti() {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    nome: "", cognome: "", telefono: "", email: "",
    servizio: "Ristrutturazione", budget: BUDGETS[1], messaggio: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/preventivi`, form);
      toast.success("Richiesta inviata con successo!", {
        description: `Grazie ${form.nome}, ti ricontatteremo al più presto al ${form.telefono}.`,
      });
      setForm({ nome: "", cognome: "", telefono: "", email: "", servizio: "Ristrutturazione", budget: BUDGETS[1], messaggio: "" });
    } catch {
      toast.error("Errore nell'invio", { description: `Riprova o chiamaci al ${PHONE}.` });
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contatti-page" className="pt-20">
      <Seo
        title="Preventivo Gratuito Ristrutturazione | Edilkrea Technoimpianti"
        description="Richiedi un preventivo gratuito per ristrutturazioni, impianti e nuove costruzioni. Sopralluogo senza impegno: ti ricontattiamo entro 24 ore."
      />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-24 pb-24 sm:pb-32">
        <SectionHead level="h1" overline="Parliamone" title="Richiedi un Preventivo" />
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
            Raccontaci il tuo progetto: ti ricontattiamo entro 24 ore lavorative
            per un sopralluogo gratuito e senza impegno.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-4">
            {INFO.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.08}>
                <div className="flex items-center gap-4 rounded-2xl border border-gold/15 bg-card2 p-5 transition-colors hover:border-gold/40">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-copper/15 border border-copper/30">
                    <item.icon className="h-5 w-5 text-copper" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold/80">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} data-testid={item.testid} className="block truncate text-sm sm:text-base font-semibold text-cream hover:text-gold transition-colors">
                        {item.value}
                      </a>
                    ) : (
                      <p data-testid={item.testid} className="text-sm sm:text-base font-semibold text-cream">{item.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <div className="rounded-2xl border border-gold/15 bg-gradient-to-br from-card2 to-panel p-6">
                <p className="font-display text-lg font-bold uppercase text-cream">Sopralluogo gratuito</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  Ogni preventivo parte da un sopralluogo sul posto: misuriamo, ascoltiamo e proponiamo la soluzione migliore.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-2xl border border-gold/15 bg-card2 p-6 sm:p-8" data-testid="quote-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="input-nome" required placeholder="Nome *" value={form.nome} onChange={set("nome")} className={INPUT_CLS} />
                <input data-testid="input-cognome" required placeholder="Cognome *" value={form.cognome} onChange={set("cognome")} className={INPUT_CLS} />
                <input data-testid="input-telefono" required type="tel" placeholder="Telefono *" value={form.telefono} onChange={set("telefono")} className={INPUT_CLS} />
                <input data-testid="input-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={INPUT_CLS} />
                <select data-testid="input-servizio" value={form.servizio} onChange={set("servizio")} className={INPUT_CLS}>
                  <option>Ristrutturazione</option>
                  <option>Impianti</option>
                  <option>Nuova Costruzione</option>
                  <option>Efficientamento Energetico</option>
                  <option>Altro</option>
                </select>
                <select data-testid="input-budget" value={form.budget} onChange={set("budget")} className={INPUT_CLS}>
                  {BUDGETS.map((b) => <option key={b}>{b}</option>)}
                </select>
              </div>
              <textarea
                data-testid="input-messaggio"
                required
                rows={5}
                placeholder="Descrivi il tuo progetto: tipologia di immobile, lavori desiderati, tempistiche... *"
                value={form.messaggio}
                onChange={set("messaggio")}
                className={`${INPUT_CLS} mt-4 resize-none`}
              />
              <button
                type="submit"
                data-testid="quote-form-submit-button"
                disabled={sending}
                className="group mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-wider text-ink transition-all hover:bg-copper hover:-translate-y-0.5 disabled:opacity-60"
              >
                {sending ? "Invio in corso..." : "Invia Richiesta"}
                <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-xs text-slate-500">
                Inviando il modulo accetti di essere ricontattato da Edilkrea Technoimpianti.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
